"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getSystemTableViewPresetById = exports.getSystemTableViewPresetByTableAndId = exports.getSystemTableViewPresets = exports.isSystemTableViewPresetId = exports.SYSTEM_TABLE_VIEW_PRESET_ID_PREFIX = void 0;
// The system table-view preset catalog. Lives in the domain layer (not the
// server TableViewService) because the definitions are plain data with
// client-safe deps — client code (e.g. the events-table preset tests) reads
// the catalog without pulling in the server barrel. The server service
// re-exports it, so server callers are unchanged.
const table_view_presets_1 = require("./table-view-presets");
exports.SYSTEM_TABLE_VIEW_PRESET_ID_PREFIX = "__langfuse_";
const isSystemTableViewPresetId = (id) => !!id?.startsWith(exports.SYSTEM_TABLE_VIEW_PRESET_ID_PREFIX);
exports.isSystemTableViewPresetId = isSystemTableViewPresetId;
const buildSystemPreset = ({ id, name, description, tableName, category, filters, orderBy = null, }) => ({
    id,
    name,
    description,
    tableName,
    category,
    state: {
        filters,
        columnOrder: [],
        columnVisibility: {},
        orderBy,
        searchQuery: "",
    },
});
const OBSERVATIONS_EVENTS_SYSTEM_TABLE_VIEW_PRESETS = [
    buildSystemPreset({
        id: `${exports.SYSTEM_TABLE_VIEW_PRESET_ID_PREFIX}errors_only`,
        name: "Errors Only",
        description: "Focus on observations that failed",
        tableName: table_view_presets_1.TableViewPresetTableName.ObservationsEvents,
        category: table_view_presets_1.SystemTableViewPresetCategory.Errors,
        filters: [
            {
                column: "level",
                type: "stringOptions",
                operator: "any of",
                value: ["ERROR"],
            },
        ],
    }),
    // --- Slow calls -----------------------------------------------------------
    buildSystemPreset({
        id: `${exports.SYSTEM_TABLE_VIEW_PRESET_ID_PREFIX}latency_over_10s`,
        name: "Latency over 10s",
        description: "Observations taking longer than 10 seconds",
        tableName: table_view_presets_1.TableViewPresetTableName.ObservationsEvents,
        category: table_view_presets_1.SystemTableViewPresetCategory.SlowCalls,
        filters: [{ column: "latency", type: "number", operator: ">", value: 10 }],
        orderBy: { column: "latency", order: "DESC" },
    }),
    buildSystemPreset({
        id: `${exports.SYSTEM_TABLE_VIEW_PRESET_ID_PREFIX}slow_generations`,
        name: "Slow generations",
        description: "Generation calls slower than 5 seconds",
        tableName: table_view_presets_1.TableViewPresetTableName.ObservationsEvents,
        category: table_view_presets_1.SystemTableViewPresetCategory.SlowCalls,
        filters: [
            {
                column: "type",
                type: "stringOptions",
                operator: "any of",
                value: ["GENERATION"],
            },
            { column: "latency", type: "number", operator: ">", value: 5 },
        ],
        orderBy: { column: "latency", order: "DESC" },
    }),
    // --- Errors ---------------------------------------------------------------
    buildSystemPreset({
        id: `${exports.SYSTEM_TABLE_VIEW_PRESET_ID_PREFIX}warnings_and_errors`,
        name: "Warnings & errors",
        description: "Observations logged at WARNING or ERROR level",
        tableName: table_view_presets_1.TableViewPresetTableName.ObservationsEvents,
        category: table_view_presets_1.SystemTableViewPresetCategory.Errors,
        filters: [
            {
                column: "level",
                type: "stringOptions",
                operator: "any of",
                value: ["ERROR", "WARNING"],
            },
        ],
    }),
    buildSystemPreset({
        // Shipped-id stability: this is the pre-chips "Generations Only" preset
        // (identical filters) renamed and categorized. Its id already lives in
        // bookmarked `?viewId=` URLs and `default_views` rows (view_id has no FK
        // on purpose), so the rename keeps the id — retiring it would strand
        // those references. Never mint a new id for a preset whose meaning
        // survives a catalog iteration — and conversely, the FILTER SEMANTICS
        // under this id are frozen: tightening this preset (e.g. adding score or
        // output filters) would silently morph every old bookmark/default, so a
        // preset with a different meaning must get a new id.
        id: `${exports.SYSTEM_TABLE_VIEW_PRESET_ID_PREFIX}generations_only`,
        name: "Review output (generations)",
        description: "LLM generation outputs, for reviewing response quality",
        tableName: table_view_presets_1.TableViewPresetTableName.ObservationsEvents,
        category: table_view_presets_1.SystemTableViewPresetCategory.Errors,
        filters: [
            {
                column: "type",
                type: "stringOptions",
                operator: "any of",
                value: ["GENERATION"],
            },
        ],
    }),
    buildSystemPreset({
        id: `${exports.SYSTEM_TABLE_VIEW_PRESET_ID_PREFIX}missed_tool_calls`,
        name: "Missed tool calls",
        description: "LLM calls that had tools available but didn't call any",
        tableName: table_view_presets_1.TableViewPresetTableName.ObservationsEvents,
        category: table_view_presets_1.SystemTableViewPresetCategory.Errors,
        filters: [
            {
                column: "type",
                type: "stringOptions",
                operator: "any of",
                value: ["GENERATION"],
            },
            { column: "toolDefinitions", type: "number", operator: ">", value: 0 },
            { column: "toolCalls", type: "number", operator: "=", value: 0 },
        ],
    }),
    // --- Cost regression ------------------------------------------------------
    buildSystemPreset({
        id: `${exports.SYSTEM_TABLE_VIEW_PRESET_ID_PREFIX}high_cost`,
        name: "High cost (> $1)",
        description: "Observations costing more than $1",
        tableName: table_view_presets_1.TableViewPresetTableName.ObservationsEvents,
        category: table_view_presets_1.SystemTableViewPresetCategory.CostRegression,
        filters: [{ column: "totalCost", type: "number", operator: ">", value: 1 }],
        orderBy: { column: "totalCost", order: "DESC" },
    }),
    buildSystemPreset({
        id: `${exports.SYSTEM_TABLE_VIEW_PRESET_ID_PREFIX}high_token_usage`,
        name: "High token usage",
        description: "Observations using more than 50k tokens",
        tableName: table_view_presets_1.TableViewPresetTableName.ObservationsEvents,
        category: table_view_presets_1.SystemTableViewPresetCategory.CostRegression,
        filters: [
            {
                column: "totalTokens",
                type: "number",
                operator: ">",
                value: 50000,
            },
        ],
        orderBy: { column: "totalTokens", order: "DESC" },
    }),
];
const SYSTEM_TABLE_VIEW_PRESETS = {
    [table_view_presets_1.TableViewPresetTableName.ObservationsEvents]: OBSERVATIONS_EVENTS_SYSTEM_TABLE_VIEW_PRESETS,
};
const getSystemTableViewPresets = (tableName) => SYSTEM_TABLE_VIEW_PRESETS[tableName] ?? [];
exports.getSystemTableViewPresets = getSystemTableViewPresets;
const getSystemTableViewPresetByTableAndId = (tableName, id) => (0, exports.getSystemTableViewPresets)(tableName).find((preset) => preset.id === id) ??
    null;
exports.getSystemTableViewPresetByTableAndId = getSystemTableViewPresetByTableAndId;
const getSystemTableViewPresetById = (id) => {
    for (const presets of Object.values(SYSTEM_TABLE_VIEW_PRESETS)) {
        const preset = presets?.find((candidate) => candidate.id === id);
        if (preset)
            return preset;
    }
    return null;
};
exports.getSystemTableViewPresetById = getSystemTableViewPresetById;
